import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Resets focus and scroll on navigation.
 *
 * In a single-page app the document never reloads, so after clicking a nav link
 * keyboard focus stays on the link you just left and the next Tab continues
 * through the sidebar rather than into the page you asked for. Screen readers
 * likewise announce nothing, because from their point of view nothing happened.
 *
 * Moving focus to the main landmark fixes all three: focus order restarts at
 * the content, the new heading is announced, and the scroll position resets the
 * way a real page load would.
 */
export function RouteFocusReset({ targetId = 'content' }: { targetId?: string }) {
  const { pathname } = useLocation();
  const first = useRef(true);

  useEffect(() => {
    // Skip the initial mount: stealing focus on cold boot would yank it away
    // from anything the browser restored, and there is nothing to announce.
    if (first.current) {
      first.current = false;
      return;
    }

    const main = document.getElementById(targetId);
    if (!main) return;
    main.focus({ preventScroll: true });
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname, targetId]);

  return null;
}
